// "What to order" per supplier: usual quantities, one-tap WhatsApp text, print/PDF order sheet, supplier phone.
import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, Printer, Phone, Loader2, Copy } from 'lucide-react';
import { apiCall } from '../_shared';

const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

const orderSheetHtml = (g, hotel, dateStr) => {
  const list = g.items;
  const rows = list.map((i) => `<tr><td>${esc(i.name)}</td><td class="r">${i.stock_units} ${esc(i.unit || '')}</td><td class="r"><b>${i.qty_units} ${esc(i.unit || 'pcs')}</b></td></tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Order ${esc(g.supplier || '')} ${dateStr}</title>
<style>body{font-family:Arial,sans-serif;margin:32px;color:#111}h1{font-size:20px;margin:0 0 4px}p{margin:2px 0;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px}th,td{padding:6px 8px;border-bottom:1px solid #ddd;text-align:left}.r{text-align:right}th{background:#f3f4f6}.foot{margin-top:24px;font-size:12px;color:#555}@media print{body{margin:12mm}}</style></head>
<body><h1>Order — ${esc(hotel)} bar</h1><p>Supplier: <b>${esc(g.supplier || 'not set')}</b>${g.phone ? ` · ${esc(g.phone)}` : ''}</p><p>Date: ${dateStr}</p>
<table><thead><tr><th>Item</th><th class="r">In stock</th><th class="r">Please deliver</th></tr></thead><tbody>${rows}</tbody></table>
<p class="foot">Delivery as usual. Thank you.</p><script>window.onload=function(){setTimeout(function(){window.print()},150)}</script></body></html>`;
};

const SupplierPhone = ({ name, phone, onSaved }) => {
  const [v, setV] = useState(phone || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await apiCall('/api/admin/bar/suppliers', { method: 'PUT', body: JSON.stringify({ name, phone: v }) }); onSaved(); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <Phone className="w-3 h-3 text-slate-400" />
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="+382 6x xxx xxx" className="border rounded px-1.5 py-0.5 text-[11px] w-32" data-testid={`reorder-phone-${name}`} />
      {v !== (phone || '') && <button type="button" onClick={save} disabled={busy} className="text-[11px] underline" data-testid={`reorder-phone-save-${name}`}>{busy ? '…' : 'save'}</button>}
    </span>
  );
};

const Group = ({ g, hotel, dateStr, onSaved }) => {
  const [copied, setCopied] = useState(false);
  const name = g.supplier || 'no supplier set';
  const list = g.items;
  const count = list.length;
  const wa = `https://wa.me/${(g.phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(g.text)}`;
  const items = list.map((i) => (
    <li key={i.id} data-testid={`bar-low-${i.id}`}>
      {i.name}: <b>{i.stock_units} {i.unit}</b> left → order <b>{i.qty_units} {i.unit || 'pcs'}</b>{i.usual_units ? <span className="text-amber-700/70"> (usual {i.usual_units})</span> : <span className="text-amber-700/70"> (to 3× threshold)</span>}
    </li>
  ));
  const print = () => {
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) return;
    w.document.open(); w.document.write(orderSheetHtml(g, hotel, dateStr)); w.document.close();
  };
  const copy = async () => { try { await navigator.clipboard.writeText(g.text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } };
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3" data-testid={`reorder-group-${g.supplier || 'none'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <p className="font-semibold text-amber-900 text-sm">{name} <span className="text-slate-400 font-normal">· {count} item{count === 1 ? '' : 's'}</span></p>
        <span className="ml-auto" />
        {g.supplier && <SupplierPhone name={g.supplier} phone={g.phone} onSaved={onSaved} />}
        <a href={g.phone ? wa : undefined} onClick={(e) => { if (!g.phone) { e.preventDefault(); copy(); } }} target="_blank" rel="noopener noreferrer"
           className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold ${g.phone ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'border border-emerald-300 text-emerald-800 hover:bg-emerald-50'}`}
           title={g.phone ? 'Open WhatsApp with the order text' : 'No phone saved — copies the order text'} data-testid={`reorder-wa-${g.supplier || 'none'}`}>
          <MessageCircle className="w-3 h-3" /> {g.phone ? 'WhatsApp' : copied ? 'Copied!' : 'Copy text'}
        </a>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 border rounded px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`reorder-copy-${g.supplier || 'none'}`}><Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}</button>
        <button type="button" onClick={print} className="inline-flex items-center gap-1 border rounded px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`reorder-pdf-${g.supplier || 'none'}`}><Printer className="w-3 h-3" /> PDF / print</button>
      </div>
      <ul className="space-y-0.5 text-amber-900 text-xs">{items}</ul>
    </div>
  );
};

export const ReorderLists = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { setData(await apiCall('/api/admin/bar/reorder')); } catch { setData({ groups: [] }); } }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return <p className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking stock…</p>;
  const groups = data.groups || [];
  if (!groups.length) return null;
  const cards = groups.map((g) => <Group key={g.supplier || 'none'} g={g} hotel={data.hotel} dateStr={data.date} onSaved={load} />);
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="bar-low-list">
      <p className="font-semibold text-amber-800 text-xs">Below threshold — what to order <span className="font-normal text-amber-700/80">· one list per supplier, usual quantities from past deliveries</span></p>
      {cards}
    </div>
  );
};

export default ReorderLists;
